import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Loader2, FileText, Download } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useTranslation } from "@/shared/i18n";
import type { CreatorReceivableSnapshot, ReceivableSnapshotStatus } from "../types";
import { Link } from "react-router-dom";
import { MockBanner } from "./MockBanner";

const currency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);

const formatMonth = (paymentMonth: string) => {
  const [y, m] = paymentMonth.split("-");
  if (!y || !m) return paymentMonth;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return format(d, "MMM yyyy", { locale: ptBR });
};

const statusMap: Record<ReceivableSnapshotStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  CONCLUDED: { label: "CONCLUDED", variant: "default" },
  CALCULATED: { label: "CALCULATED", variant: "secondary" },
  WAITING: { label: "WAITING", variant: "outline" },
};

interface ReceivablesDashboardProps {
  snapshots: CreatorReceivableSnapshot[];
  isLoading: boolean;
  periodMonths: number;
  onPeriodChange: (months: number) => void;
}

export function ReceivablesDashboard({
  snapshots,
  isLoading,
  periodMonths,
  onPeriodChange,
}: ReceivablesDashboardProps) {
  const { t } = useTranslation("creator-financial");

  const chartData = useMemo(() => {
    return [...(snapshots ?? [])]
      .sort(
        (a, b) =>
          new Date(a.paymentMonth).getTime() - new Date(b.paymentMonth).getTime()
      )
      .map((s) => ({
        month: formatMonth(s.paymentMonth),
        shareAmount: s.shareAmount,
        grossAmount: s.grossAmount,
        netAmount: s.netAmount,
        sharePercentage: s.sharePercentage,
      }));
  }, [snapshots]);

  const totalReceived = useMemo(() => {
    return snapshots.reduce((acc, s) => acc + s.shareAmount, 0);
  }, [snapshots]);

  const lastMonthSnapshot = useMemo(() => {
    const sorted = [...snapshots].sort(
      (a, b) =>
        new Date(b.paymentMonth).getTime() - new Date(a.paymentMonth).getTime()
    );
    return sorted[0];
  }, [snapshots]);

  const monthlyAverage = useMemo(() => {
    if (snapshots.length === 0) return 0;
    return totalReceived / snapshots.length;
  }, [snapshots.length, totalReceived]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <Loader2 className="h-8 w-8 animate-spin opacity-45" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <MockBanner />
      <div>
        <h1 className="cf-page-title">{t("receivables.title")}</h1>
        <p className="cf-page-subtitle">{t("receivables.subtitle")}</p>
        <p className="text-sm mt-1 text-[var(--cf-text-muted)]">
          {t("receivables.receiptActionsHint")}
        </p>
      </div>

      {/* Cards */}
      <div className="grid gap-5 md:grid-cols-3">
        <div className="cf-card">
          <div className="flex flex-row items-center justify-between pb-2">
            <span className="cf-label">{t("receivables.totalReceived")}</span>
            <TrendingUp className="h-4 w-4 text-[var(--cf-success)]" />
          </div>
          <div className="cf-value text-2xl font-bold mt-1">{currency(totalReceived)}</div>
          <p className="text-xs mt-1 opacity-65">{t("receivables.totalReceivedHint")}</p>
        </div>
        <div className="cf-card">
          <div className="flex flex-row items-center justify-between pb-2">
            <span className="cf-label">{t("receivables.lastMonth")}</span>
          </div>
          <div className="cf-value text-2xl font-bold mt-1">
            {lastMonthSnapshot
              ? currency(lastMonthSnapshot.shareAmount)
              : currency(0)}
          </div>
          <p className="text-xs mt-1 opacity-65">
            {lastMonthSnapshot
              ? formatMonth(lastMonthSnapshot.paymentMonth)
              : "—"}
          </p>
        </div>
        <div className="cf-card">
          <div className="flex flex-row items-center justify-between pb-2">
            <span className="cf-label">{t("receivables.monthlyAverage")}</span>
          </div>
          <div className="cf-value text-2xl font-bold mt-1">{currency(monthlyAverage)}</div>
          <p className="text-xs mt-1 opacity-65">{t("receivables.monthlyAverageHint")}</p>
        </div>
      </div>

      {/* Gráfico */}
      <div className="cf-card">
        <div className="flex flex-row items-center justify-between pb-4">
          <h2 className="text-lg font-semibold text-[var(--cf-text)]">{t("receivables.evolutionTitle")}</h2>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={periodMonths === 6 ? "default" : "outline"}
              size="sm"
              onClick={() => onPeriodChange(6)}
            >
              {t("receivables.period6Months")}
            </Button>
            <Button
              type="button"
              variant={periodMonths === 12 ? "default" : "outline"}
              size="sm"
              onClick={() => onPeriodChange(12)}
            >
              {t("receivables.period1Year")}
            </Button>
          </div>
        </div>
        <div>
          {chartData.length === 0 ? (
            <div className="h-[320px] flex items-center justify-center text-[var(--cf-text-muted)]">
              {t("receivables.empty")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--cf-border)" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "var(--cf-text-secondary)" }}
                  tickFormatter={(v) => (typeof v === "string" ? v.slice(0, 6) : v)}
                />
                <YAxis
                  tickFormatter={(v) => (v >= 1000 ? `${v / 1000}K` : String(v))}
                  tick={{ fontSize: 12, fill: "var(--cf-text-secondary)" }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="rounded-[var(--cf-radius-card)] border border-[var(--cf-border)] bg-[var(--cf-surface)] p-3 shadow-lg">
                        <p className="font-medium mb-2 text-[var(--cf-text)]">{d.month}</p>
                        <p className="text-sm text-[var(--cf-text-secondary)]">
                          {t("receivables.tooltipValueToReceive")}: {currency(d.shareAmount)} ({d.sharePercentage}%)
                        </p>
                      </div>
                    );
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="shareAmount"
                  stroke="var(--cf-primary)"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tabela resumo */}
      <div className="cf-card">
        <div className="flex flex-row items-center justify-between pb-4">
          <h2 className="text-lg font-semibold text-[var(--cf-text)]">{t("receivables.monthlyHistory")}</h2>
          <Button type="button" variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            {t("receivables.exportCsv")}
          </Button>
        </div>
        <div>
          {snapshots.length === 0 ? (
            <p className="text-center py-8 text-[var(--cf-text-muted)]">
              {t("receivables.empty")}
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--cf-border)] hover:bg-transparent">
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.month")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.status")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.valueToReceive")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...snapshots]
                    .sort(
                      (a, b) =>
                        new Date(b.paymentMonth).getTime() -
                        new Date(a.paymentMonth).getTime()
                    )
                    .slice(0, 10)
                    .map((s) => {
                      const status = statusMap[s.status] ?? {
                        label: s.status,
                        variant: "outline" as const,
                      };
                      const badgeClass =
                        status.variant === "default"
                          ? "cf-badge-pill cf-badge-ativa"
                          : status.variant === "secondary"
                            ? "cf-badge-pill cf-badge-default"
                            : "cf-badge-pill cf-badge-inativa";
                      return (
                        <TableRow key={s.paymentMonth} className="border-[var(--cf-border)]">
                          <TableCell className="text-[var(--cf-text)]">{formatMonth(s.paymentMonth)}</TableCell>
                          <TableCell>
                            <span className={badgeClass}>{status.label}</span>
                          </TableCell>
                          <TableCell className="text-[var(--cf-text)]">
                            {currency(s.shareAmount)}{" "}
                            <span className="text-xs text-[var(--cf-text-muted)]">
                              ({t("receivables.valueToReceiveWithPercent", { percent: s.sharePercentage })})
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button type="button" variant="ghost" size="icon">
                              <FileText className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              <div className="mt-4">
                <Button variant="link" className="h-auto p-0 text-sm" asChild>
                  <Link to="/creator/payments">
                    {t("receivables.viewFullHistory")}
                  </Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
